/**
 * MyGurukul → Notes Taken. Every note Study Buddy has taken, per subject and
 * chapter (never per conversation), as pointers under the chapter's headings.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, ChevronDown, Loader2, Moon, NotebookPen } from 'lucide-react';
import { toast } from 'sonner';
import { deleteNote, fetchNotes, fetchTonight, markTonightDone } from '@/lib/notes';
import NoteTree from '@/components/notes/NoteTree';

function ChapterCard({ chapter, onDelete, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-3xl border border-amber-100 bg-white shadow-[0_1px_2px_rgba(15,23,42,.04)]" data-testid={`notes-chapter-${chapter.chapter}`}>
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5 text-left" data-testid={`notes-chapter-toggle-${chapter.chapter}`}>
        <span className="font-heading text-sm sm:text-base font-bold text-slate-900">{chapter.chapter}</span>
        <span className="flex items-center gap-2 text-[11px] font-heading text-amber-700">
          {chapter.count} point{chapter.count === 1 ? '' : 's'}
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {open && <div className="px-4 sm:px-5 pb-4 border-t border-amber-50 pt-3"><NoteTree headings={chapter.headings} onDelete={onDelete} /></div>}
    </div>
  );
}

export default function NotesPage() {
  const [params, setParams] = useSearchParams();
  const todayOnly = params.get('tonight') === '1';
  const [groups, setGroups] = useState(null);
  const [subject, setSubject] = useState('');
  const [tonight, setTonight] = useState(null);

  const load = useCallback(async () => {
    try {
      const [n, t] = await Promise.all([fetchNotes({ today: todayOnly }), fetchTonight()]);
      setGroups(n.groups || []); setTonight(t);
    } catch (e) { toast.error(e?.message || 'Could not load your notes'); setGroups([]); }
  }, [todayOnly]);
  useEffect(() => { load(); }, [load]);

  const subjects = useMemo(() => (groups || []).map(g => g.subject), [groups]);
  const active = (groups || []).find(g => g.subject === subject) || (groups || [])[0];

  const remove = async (id) => {
    try { await deleteNote(id); load(); } catch { toast.error('Could not remove that point'); }
  };
  const done = async () => {
    try { setTonight(await markTonightDone().then(r => ({ ...tonight, ...r }))); toast.success('Lovely — sleep on it!'); } catch { toast.error('Please try again'); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6" data-testid="notes-page">
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight flex items-center gap-3">
        <NotebookPen className="w-8 h-8 text-amber-500" /> Notes Taken
      </h1>
      <p className="font-body text-sm text-slate-500 mt-1">Everything your Study Buddy noted for you — by subject and chapter.</p>

      {tonight?.count > 0 && (
        <div className="mt-5 rounded-3xl bg-indigo-950 text-white p-4 sm:p-5 flex items-center justify-between gap-3" data-testid="notes-tonight-banner">
          <div className="flex items-center gap-3">
            <Moon className="w-6 h-6 text-amber-300 flex-shrink-0" />
            <p className="font-body text-sm">You noted <b>{tonight.count}</b> point{tonight.count === 1 ? '' : 's'} today. Read them once before bed.</p>
          </div>
          {tonight.reviewed ? (
            <span className="text-xs font-heading text-emerald-300 flex items-center gap-1" data-testid="notes-tonight-done"><CheckCircle2 className="w-4 h-4" /> Done</span>
          ) : (
            <div className="flex gap-2 flex-shrink-0">
              {!todayOnly && <button onClick={() => setParams({ tonight: '1' })} data-testid="notes-show-today" className="text-xs font-heading px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20">Show today's</button>}
              <button onClick={done} data-testid="notes-mark-read" className="text-xs font-heading px-3 py-1.5 rounded-full bg-amber-400 text-amber-950 hover:bg-amber-300">I've read them</button>
            </div>
          )}
        </div>
      )}
      {todayOnly && <button onClick={() => setParams({})} className="mt-3 text-xs font-heading text-indigo-600 hover:underline" data-testid="notes-show-all">← Show all my notes</button>}

      {groups === null ? (
        <div className="mt-10 flex items-center gap-2 text-slate-400" data-testid="notes-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : !groups.length ? (
        <div className="mt-8 rounded-3xl border border-dashed border-amber-200 bg-amber-50/40 p-6 text-center" data-testid="notes-empty">
          <p className="font-heading text-slate-800">No notes yet</p>
          <p className="font-body text-sm text-slate-500 mt-1">While chatting with your Study Buddy, tap <b>Take Note</b> or select any words in a reply.</p>
        </div>
      ) : (
        <>
          <div className="mt-6 flex flex-wrap gap-2" data-testid="notes-subject-tabs">
            {subjects.map(s => (
              <button key={s} onClick={() => setSubject(s)} data-testid={`notes-subject-${s}`}
                className={`px-4 py-1.5 rounded-full text-xs font-heading border transition-colors ${active?.subject === s ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>
                {s}
              </button>
            ))}
          </div>
          <div className="mt-4 space-y-3">
            {active?.chapters.map((c, i) => <ChapterCard key={c.chapter} chapter={c} onDelete={remove} defaultOpen={i === 0} />)}
          </div>
        </>
      )}
    </div>
  );
}
